export interface APIPermissionViewModel {
    Id: number;
    Name: string;
    Description: string;
    RoleTypePermissions: RoleTypePermission[];
}

export interface RoleTypePermission {
    RoleTypeId: number;
    PermissionId: number;
}

const supplierPortalPermissions = [
    'SP_LOGIN',
    'SP_ROLE_TYPE_MANAGEMENT_VIEW',
    'SP_ROLE_TYPE_MANAGEMENT_EDIT',
    'SP_USER_MANAGEMENT_VIEW',
    'SP_USER_MANAGEMENT_EDIT',
    'SP_INVOICE_PROFILE_VIEW',
    'SP_INVOICE_PROFILE_EDIT',
    'SP_PO_POA_PROFILE_VIEW',
    'SP_PO_POA_PROFILE_EDIT',
    'SP_ASN_PROFILE_MANAGEMENT_VIEW',
    'SP_ASN_PROFILE_MANAGEMENT_EDIT',
    'SP_ACCOUNT_PROFILE_VIEW',
    'SP_ACCOUNT_PROFILE_EDIT',
    'SP_VIRTUAL_PRINTER_DOWNLOAD_VIEW',
    'SP_CONFIGURATION_SETTINGS_VIEW_EDIT',
    'SP_DOCUMENT_EDIT',
] as const;

const transceptaManagerPermissions = [
    'BULK_FEED_UPLOAD',
    'CREATE_UPDATE_DELETE_ERP_GATEWAY_CONFIGURATION',
    'CREATE_UPDATE_DELETE_ERP_API_READ_CONFIGURATION',
    'CREATE_UPDATE_DELETE_VENDOR',
    'CREATE_WORKFLOW_DOCUMENT',
    'VIEW_CONFIGURABLE_JSRULES_MAPPING',
    'TM_LOGIN',
    'TM_ROLE_TYPE_MANAGEMENT_VIEW',
    'TM_ROLE_TYPE_MANAGEMENT_EDIT',
    'TM_ACCOUNT_PROFILE_VIEW',
    'TM_ACCOUNT_PROFILE_EDIT',
    'TM_ASN_PROFILE_MANAGEMENT_VIEW',
    'TM_CUSTOMER_PROFILE_MANAGEMENT_VIEW',
    'TM_CUSTOMER_PROFILE_MANAGEMENT_EDIT',
    'TM_SIM_DOCUMENT_SCHEDULE_MANAGEMENT_VIEW',
    'TM_SIM_DOCUMENT_SCHEDULE_MANAGEMENT_EDIT',
    'TM_QUESTIONNAIRE_MANAGEMENT_VIEW',
    'TM_QUESTIONNAIRE_MANAGEMENT_EDIT',
    'TM_CONFIGURABLE_WORKFLOWS_VIEW',
    'TM_CONFIGURABLE_WORKFLOWS_EDIT',
    'TM_VENDOR_APPROVAL_WORKFLOWS_VIEW',
    'TM_VENDOR_APPROVAL_WORKFLOWS_EDIT',
    'TM_PAYMENTS_VIEW_EDIT_CREATE_DRAFT_PAYMENTS',
    'TM_NOTIFICATION_MANAGEMENT_VIEW',
    'TM_NOTIFICATION_MANAGEMENT_EDIT',
    'TM_PAYMENTS_SUBMIT_PAYMENTS',
    'TM_PARK_DOCUMENT_ACT_ON_BEHALF_OF_ASSIGNED_USER',
    'TM_VENDOR_VIEW',
    'TM_VENDOR_EDIT',
    'TM_VENDOR_SITE_VIEW',
    'TM_VENDOR_SITE_EDIT',
    'TM_USER_MANAGEMENT_VIEW',
    'TM_USER_MANAGEMENT_EDIT',
    'TM_USER_GROUP_MANAGEMENT_VIEW',
    'TM_USER_GROUP_MANAGEMENT_EDIT',
    'TM_PURGE_TEST_DATA_VIEW_EDIT',
    'TM_PARKING_LOT_ONLY_MODE_VIEW',
    'TM_BUSINESS_DOC_TYPE_MAPPING_MANAGE',
    'TM_VIEW_CREATE_EDIT_SIM_DOCUMENTS',
] as const;

const cspPermissions = [
    'CSP_FIELD_DICTIONARY_MANAGE',
    'CSP_SUPPLIER_SIGNUP_LANDING_PAGE_VIEW',
    'CSP_SUPPLIER_SIGNUP_LANDING_PAGE_EDIT',
    'CSP_SUPPLIER_SIGNUP_SUBMISSION_GUIDE_VIEW',
    'CSP_SUPPLIER_SIGNUP_SUBMISSION_GUIDE_EDIT',
    'CSP_COMPANY_CUSTOM_DATASET_STRUCTURES_VIEW',
    'CSP_COMPANY_CUSTOM_DATASET_STRUCTURES_EDIT',
    'CSP_DASHBOARD_REPORT_METADATA_VIEW',
    'CSP_DASHBOARD_REPORT_METADATA_EDIT',
    'CSP_ERP_CONFIGURATION_GATEWAY_VIEW',
    'CSP_ERP_CONFIGURATION_API_READ_VIEW',
    'CSP_HELP_CONTENT_VIEW',
    'CSP_HELP_CONTENT_EDIT',
    'CSP_ROLE_TYPE_VIEW',
    'CSP_ROLE_TYPE_EDIT',
    'CSP_FIELD_CATEGORIZATION_VIEW',
    'CSP_FIELD_CATEGORIZATION_EDIT',
    'CSP_FIELD_CATEGORIZATION_GENERAL_PROMPT_EDIT',
    'CSP_FIELD_CATEGORIZATION_BUYER_PROMPT_EDIT',
    'CSP_WORKFLOW_VIEW',
    'CSP_WORKFLOW_EDIT',
    'CSP_VENDOR_APPROVAL_WORKFLOW_VIEW',
    'CSP_VENDOR_APPROVAL_WORKFLOW_EDIT',
    'CSP_COMPANY_GL_ACCOUNT_STRUCTURE_VIEW',
    'CSP_COMPANY_GL_ACCOUNT_STRUCTURE_EDIT',
    'CSP_VALIDATE_SAMPLES_VIEW',
    'CSP_ATTRIBUTE_CONFIGURATION_VIEW',
    'CSP_ATTRIBUTE_CONFIGURATION_EDIT',
    'CSP_DOCUMENT_FLIP_VIEW',
    'CSP_DOCUMENT_FLIP_EDIT',
    'CSP_BUSINESS_DOCUMENT_TYPE_MANAGEMENT_VIEW',
    'CSP_BUSINESS_DOCUMENT_TYPE_MANAGEMENT_EDIT',
    'CSP_BUSINESS_DOCUMENT_TYPE_SEARCH_MANAGEMENT_VIEW',
    'CSP_BUSINESS_DOCUMENT_TYPE_SEARCH_MANAGEMENT_EDIT',
    'CSP_VALIDATION_FAILURE_TYPES_VIEW',
    'CSP_VALIDATION_FAILURE_TYPES_EDIT',
    'CSP_DASHBOARD_REJECTION_GROUPS_VIEW',
    'CSP_DASHBOARD_REJECTION_GROUPS_EDIT',
    'CSP_CUSTOM_JS_RULES_V1_VIEW',
    'CSP_CUSTOM_JS_RULES_V1_EDIT',
    'CSP_CUSTOM_JS_RULES_MAPPING_V1_VIEW',
    'CSP_CUSTOM_JS_RULES_MAPPING_V1_EDIT',
    'CSP_CUSTOM_JS_RULES_V2_VIEW',
    'CSP_CUSTOM_JS_RULES_V2_EDIT',
    'CSP_CUSTOM_JS_RULES_MAPPING_V2_VIEW',
    'CSP_CUSTOM_JS_RULES_MAPPING_V2_EDIT',
    'CSP_SUPPLIER_TRADING_PARTNERS_VIEW',
    'CSP_SUPPLIER_TRADING_PARTNERS_EDIT',
    'CSP_BUYER_SEARCH_VIEW',
    'CSP_SUPPLIER_SEARCH_VIEW',
] as const;

export const APIPermissionList = [
    'RUN_DASHBOARD_REPORT',
    'IGNORE_USERGROUP_BUSINESSDOCTYPESEARCH_FILTER',
    'HIDE_DASHBOARD',
    ...supplierPortalPermissions,
    ...transceptaManagerPermissions,
    ...cspPermissions,
] as const;

export type APIPermission = typeof APIPermissionList[number];
